package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.layout.engine.UiPerfSink
import top.wcpe.allincore.ui.runtime.crossplatform.RuntimeNodePaths
import top.wcpe.allincore.ui.runtime.element.ChildOrigin
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.Modal
import top.wcpe.allincore.ui.runtime.element.ScrollableContainer
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.TabView
import top.wcpe.allincore.ui.runtime.element.Toast
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.material.MaterialInteractionState
import top.wcpe.allincore.ui.runtime.style.VisualModifier
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.internal.time.nanoTime
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.layer.LayerBufferingBridge
import top.wcpe.allincore.ui.runtime.render.layer.LayerRenderers
import top.wcpe.allincore.ui.runtime.render.model.RenderLayer
import top.wcpe.allincore.ui.runtime.style.ScopedVisualModifier
import top.wcpe.allincore.ui.runtime.theme.FontAccess
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic
import kotlin.math.max

/**
 * 渲染编排器：按 DFS 顺序遍历 [UIElement] 树，将每个节点的相对包围盒
 * 累加为绝对坐标，再委托 [RendererRegistry] 中对应的 [Renderer]
 * 通过 [UiRenderBridge] 完成绘制。
 */
@Suppress(
    "TooManyFunctions",
    "MagicNumber",
    "LongParameterList",
    "LongMethod",
    "CyclomaticComplexMethod",
    "ComplexCondition",
    "NestedBlockDepth",
    "ReturnCount",
)
object UIRenderer {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.render.UIRenderer")
    private const val OVERLAY_NODE_PATH = "runtime/overlay"

    /**
     * 隔离单个节点的渲染：捕获 [Renderer.render] 抛出的任何异常，记 ERROR 中文日志
     * （含元素 id / 类型 / 绝对坐标）后吞掉，使一个坏元素不会崩掉整帧、不会中断兄弟节点渲染。
     *
     * 降级语义：本节点绘制可能残缺，但遍历继续；GL 状态由外层 [RenderStateStack] 的
     * try/finally 与各 push/pop 成对接缝负责复位，本方法不额外触碰桥状态。
     */
    private fun renderNodeIsolated(
        renderer: Renderer,
        element: UIElement<*>,
        absolute: Box,
        bridge: UiRenderBridge,
        context: RenderContext,
    ) {
        try {
            renderer.render(element, absolute, bridge, context)
        } catch (t: Throwable) {
            logRenderFailure(element, absolute, t)
        }
    }

    /** 记录单节点渲染失败：元素 id / 类型 / 绝对坐标，便于定位坏元素。 */
    private fun logRenderFailure(element: UIElement<*>?, absolute: Box?, t: Throwable) {
        val id = if (element?.id() == null) "(匿名)" else element.id()
        val type = if (element == null) "(null)" else element::class.simpleName
        val x = absolute?.x() ?: 0f
        val y = absolute?.y() ?: 0f
        LOGGER.error(
            "[ui] 元素 [$id]（类型 $type）渲染失败，已跳过该节点继续渲染兄弟节点，绝对坐标 ($x, $y)",
            t,
        )
    }

    /**
     * 解析元素的实际渲染层：`aboveItem=true` 且渲染器为单层（`rendererLayer != null`）时
     * 改投到 [RenderLayer.OVERLAY]——该层经 [LayerBufferingBridge] 整体抬高 z，盖住
     * vanilla 物品图标（z≈150），把数量/标签等叠加文本提到物品上层；否则用渲染器自身声明的层。
     *
     * 仅覆盖单层元素；[LayerRenderers] 这类多层
     * 复合渲染器返回 `null`，不在此覆盖（保持 `null` 让其自行切层）。
     */
    @JvmStatic
    @JvmName("resolveLayer")
    internal fun resolveLayer(rendererLayer: RenderLayer?, aboveItem: Boolean): RenderLayer? {
        if (rendererLayer != null && aboveItem) {
            return RenderLayer.OVERLAY
        }
        return rendererLayer
    }

    /**
     * 保留可由 LayerBufferingBridge 安全直通降级的作用域 modifier。
     *
     * 能力查询仍在真实决策点执行，使不支持能力可以绑定到后续实际 DrawRecord；ColorFilter 继续由
     * RenderStateStack 负责，避免重复分派色彩矩阵。
     */
    private fun supportedScopedModifiers(
        scoped: List<ScopedVisualModifier>,
        bridge: UiRenderBridge,
    ): List<ScopedVisualModifier> = scoped.filter { modifier ->
        when (modifier) {
            is ScopedVisualModifier.Blur -> bridge.supportsFilterGroup()
            is ScopedVisualModifier.BlendMode -> bridge.supportsBlendMode()
            is ScopedVisualModifier.ColorFilter -> false
            is ScopedVisualModifier.History -> bridge.supportsHistoryCapture()
        }
    }

    /**
     * 渲染整棵 UI 树。
     *
     * @param root     根元素
     * @param layout   布局结果（[LayoutResult.boxOf] 返回相对父节点坐标）
     * @param bridge   平台桥接
     * @param registry 渲染器注册表
     */
    @JvmStatic
    fun render(root: UIElement<*>, layout: LayoutResult, bridge: UiRenderBridge, registry: RendererRegistry) {
        render(root, layout, bridge, registry, RenderContext.NONE)
    }

    /**
     * 渲染入口：带渲染上下文。平台层调用此重载提供 [RenderContext]，
     * 让 [SlotRenderer] 等渲染器能查询槽内物品 / 失败反馈 / cursor 状态。
     *
     * 声明式 toast 元素与 manager 活跃队列由 `ToastLayer` 统一绘制；
     * 普通 DFS 跳过 Toast 节点（与 Modal 同策略）。ToastManager 队列由平台层单独驱动
     * （见 `ToastLayer.renderManagerOnly`），不经本入口。
     *
     * 生成一次 `nowMs` 时间戳并下发给 `ModalLayer`
     * 与 `ToastLayer`，用于淡入插值；同帧内多层共享同一时间戳，避免毫秒级抖动。
     */
    @JvmStatic
    @Suppress("LongMethod")
    fun render(
        root: UIElement<*>,
        layout: LayoutResult,
        bridge: UiRenderBridge,
        registry: RendererRegistry,
        context: RenderContext?,
    ) {
        val ctx = context ?: RenderContext.NONE
        // D1 埋点：仅 dev-debug 启用时取起始时间戳，关闭时连 nanoTime 都不调，热路径零开销。
        val perfStartNs = if (UiPerfSink.isEnabled()) nanoTime() else 0L
        val nowMs = currentTimeMillis()
        // 把当前 RendererRegistry 携带的 ControlTheme 压入 ThreadLocal，让交互控件 Renderer
        // 通过 ThemeAccess.current() 取色。try/finally 保证嵌套渲染时恢复外层主题。
        val previous = ThemeAccess.current()
        ThemeAccess.set(registry.controlTheme())
        // 字体层级压栈：全局 + 页面
        val previousGlobalFont = FontAccess.global()
        val previousPageFont = FontAccess.page()
        if (registry.globalFont() != null) {
            FontAccess.setGlobal(registry.globalFont())
        }
        if (registry.pageFont() != null) {
            FontAccess.setPage(registry.pageFont())
        }
        try {
            val layerBridge = LayerBufferingBridge(bridge)
            layerBridge.beginFrame()
            val overlays = DropdownOverlayQueue()
            val tooltip = TooltipRenderer.Candidate()
            val passState = RenderStateStack()
            val nodePaths = runtimeNodePaths(root, layerBridge)
            renderRecursive(
                root, layout, layerBridge, registry, ctx, 0f, 0f,
                overlays, tooltip, passState, nodePaths, 0, false,
            )
            // 切换到浮层并锁定：Dropdown overlay / modal / toast / tooltip 及其内容子树统一绘制到
            // FLOATING 层。锁定后内容子树里默认 BACKGROUND 层的 Box/Text 无法把当前层切回底层，
            // 避免浮层被随后回放的物品图标穿透（issue #48）。
            layerBridge.beginFloatingPhase()
            overlays.render(layerBridge, registry)
            // 浮层编排经接缝下发（ADR-0072 决策 4）：JVM 委托 OverlayLayer/ToastLayer，JS 空转。
            val overlay = overlayOrchestrator()
            RuntimeNodePaths.withCapture(layerBridge, OVERLAY_NODE_PATH) {
                // modal/menu 在 Dropdown 之后、Toast 之前渲染。
                overlay.renderModalAndMenus(root, layout, layerBridge, registry, ctx, nowMs)
                // toast 层在 modal/menu 之后、tooltip 之前。
                val declarativeToasts: List<Toast> = overlay.collectVisibleToasts(root)
                overlay.renderToasts(declarativeToasts, layerBridge, nowMs)
                overlay.renderTooltips(root, layout, tooltip, layerBridge, registry, ctx, nowMs)
            }
            layerBridge.endFrame()
            bridge.flush()
        } finally {
            ThemeAccess.set(previous)
            FontAccess.setGlobal(previousGlobalFont)
            FontAccess.setPage(previousPageFont)
            // D1 埋点：记录本帧渲染耗时（含 overlay/modal/toast/tooltip 全部层）。
            if (UiPerfSink.isEnabled()) {
                UiPerfSink.recordRender(nanoTime() - perfStartNs)
            }
        }
    }

    /**
     * modal 子树的包外入口，仅供 `ModalLayer` 调用。
     *
     * 从 modal 自身的绝对定位出发递归绘制其子节点，不再走 [render] 的根入口。
     */
    @JvmStatic
    @JvmName("renderModalChild")
    internal fun renderModalChild(
        child: UIElement<*>,
        layout: LayoutResult,
        bridge: UiRenderBridge,
        registry: RendererRegistry,
        context: RenderContext,
        originX: Float,
        originY: Float,
    ) {
        renderOverlayChild(child, layout, bridge, registry, context, originX, originY)
    }

    @JvmStatic
    @JvmName("renderOverlayChild")
    internal fun renderOverlayChild(
        child: UIElement<*>,
        layout: LayoutResult,
        bridge: UiRenderBridge,
        registry: RendererRegistry,
        context: RenderContext,
        originX: Float,
        originY: Float,
    ) {
        val overlays = DropdownOverlayQueue()
        renderOverlayChildInto(child, layout, bridge, registry, context, originX, originY, overlays)
        overlays.render(bridge, registry)
    }

    /**
     * 渲染单个 overlay/modal 子节点，把展开的 dropdown 面板收集进调用方提供的队列而不出队。
     *
     * 供 modal/menu 编排层共享同一个面板队列：先画完全部兄弟内容，再统一出队渲染面板，
     * 否则先声明的 dropdown 面板会被后声明的兄弟元素整个盖住。
     */
    @JvmStatic
    @JvmName("renderModalChildInto")
    internal fun renderModalChildInto(
        child: UIElement<*>,
        layout: LayoutResult,
        bridge: UiRenderBridge,
        registry: RendererRegistry,
        context: RenderContext,
        originX: Float,
        originY: Float,
        overlays: DropdownOverlayQueue,
    ) = renderOverlayChildInto(child, layout, bridge, registry, context, originX, originY, overlays)

    @JvmStatic
    @JvmName("renderOverlayChildInto")
    internal fun renderOverlayChildInto(
        child: UIElement<*>,
        layout: LayoutResult,
        bridge: UiRenderBridge,
        registry: RendererRegistry,
        context: RenderContext,
        originX: Float,
        originY: Float,
        overlays: DropdownOverlayQueue,
    ) {
        val tooltip = TooltipRenderer.Candidate()
        val passState = RenderStateStack()
        val nodePaths = runtimeNodePaths(child, bridge)
        renderRecursive(
            child, layout, bridge, registry, context, originX, originY,
            overlays, tooltip, passState, nodePaths, 0, false,
        )
    }

    /**
     * 渲染拖拽 ghost：把给定元素子树从 (originX, originY) 起绘制（供 [DragGhostLayer] 调用）。
     *
     * 调用方负责在外层 push 好 transform（平移到鼠标）+ alpha（半透明）。本方法只负责子树绘制，
     * 不再走主树的 overlay/modal/toast 链路。
     */
    @JvmStatic
    fun renderGhost(
        element: UIElement<*>,
        layout: LayoutResult,
        bridge: UiRenderBridge,
        registry: RendererRegistry,
        context: RenderContext,
        originX: Float,
        originY: Float,
    ) {
        renderOverlayChild(element, layout, bridge, registry, context, originX, originY)
    }

    /**
     * DFS：先绘制当前元素，再递归子元素，子元素坐标在父原点基础上累加。
     *
     * @param slotAncestor 当前元素是否处于某个 [Slot] 子树内。Slot 自身把物品图标发到
     *                     ITEM 层、状态叠加发到 OVERLAY 层（z 高于子组件默认的 BACKGROUND 层），
     *                     若子组件不感知 Slot 祖先就会被物品/材质压在下方（issue #47）。为此
     *                     在 Slot 子树内的单层渲染器统一按 `aboveItem` 提升到 OVERLAY，
     *                     使内嵌 text/image 等默认绘制在物品之上。
     */
    @Suppress("LongMethod", "CyclomaticComplexMethod", "ComplexCondition", "NestedBlockDepth", "ReturnCount")
    private fun renderRecursive(
        element: UIElement<*>,
        layout: LayoutResult,
        bridge: UiRenderBridge,
        registry: RendererRegistry,
        context: RenderContext,
        originX: Float,
        originY: Float,
        overlays: DropdownOverlayQueue,
        tooltip: TooltipRenderer.Candidate,
        passState: RenderStateStack,
        nodePaths: Map<UIElement<*>, String>?,
        depth: Int,
        slotAncestor: Boolean,
    ) {
        if (!element.visible()) {
            return
        }
        val rel = layout.boxOf(element) ?: return
        // M6-f §8.2：visible=false 整棵子树短路，不渲染也不递归。布局引擎已在 buildTree
        // 阶段跳过同样的节点；此处兜底防御兼任直接调用 UIRenderer.render 的测试路径。
        if (!element.visible()) {
            return
        }
        // M6-h Round 1：modal 由 ModalLayer 单独渲染，正常 DFS 直接跳过整棵 modal 子树。
        // 但仅对作为子节点出现的 modal 生效；ModalLayer 入口经由 renderModalChild 触发，
        // 其首个 element 即 modal 的子节点，不会再次进入此分支。
        if (element is Modal) {
            return
        }
        // M6-h Round 2：toast 由 ToastLayer 统一渲染（含声明式与内建队列），DFS 跳过子树。
        if (element is Toast) {
            return
        }
        val absX = originX + rel.x()
        val absY = originY + rel.y()
        val absolute = Box(absX, absY, rel.width(), rel.height())

        // 收集 tooltip 候选（取层级最深的悬浮元素）
        tooltip.consider(element, absolute, depth)

        // ScrollableContainer（ScrollView / VirtualList / ...）在绘制前更新 viewport / content，影响后续 clamp。
        if (element is ScrollableContainer<*>) {
            element.viewportWidth(absolute.width())
            element.viewportHeight(absolute.height())
            // 全量容器（ScrollView）由渲染端遍历子节点测量内容尺寸；
            // 虚拟容器（VirtualList 等）按数据量自算，跳过测量。
            if (element.requiresContentMeasure()) {
                element.contentHeight(computeContentHeight(element, layout))
            }
            // scrollIntoView 延迟兑现：拿到 LayoutResult 后按目标子节点局部坐标算出目标 scroll。
            resolvePendingScrollIntoView(element, layout)
        }

        // overflow=hidden：用父级 transform 计算裁剪框（不含元素自身 scale），
        // 裁剪普通分支子树到自身包围盒。必须在 passState.push 之前计算——push 后
        // transform 会叠加元素自身缩放（如 Zoomable 的 zoom），导致裁剪框被放大错位。
        // ScrollableContainer / TabView 分支自带专门裁剪，提前 return，不受此影响。
        var overflowClip: FloatArray? = null
        if (element.overflow() == UIElement.Overflow.HIDDEN) {
            overflowClip = passState.transformedBox(absX, absY, absolute.width(), absolute.height())
        }

        RuntimeNodePaths.withCapture(bridge, nodePaths?.get(element)) {
            passState.push(bridge, element, absolute).use {
                // 压入元素自身字体到 FontAccess 环境层：对本元素文字与所有子孙级联生效；
                // 无字体的 4 参 drawText 也能经 resolve(null) 拿到「元素>祖先>页面>全局」字体。
                // 注意下方多处 return（ScrollableContainer / TabView），用 try/finally 确保 pop。
                val prevFont = FontAccess.pushElement(element.font())
                try {
                    val modifierChain = element.style().modifiers()
                    val renderSink = RenderBridgeSink(bridge)
                    // 回植 a1ad1b6b4：粒子池按实际元素实例隔离。
                    // DSL 循环中的静态 id 会重复；identityHash 保证同一逻辑 id 的不同实例不共享粒子实例。
                    val identityElementId = "@" + identityHashOf(element)
                    // historyLayer 需要元素跨帧稳定 id 作环形缓冲键：优先显式 id，缺失时退化为元素身份哈希。
                    val historyElementId = element.id() ?: identityElementId
                    val particleElementId = (element.id() ?: "") + identityElementId
                    val scopedScope = ModifierDispatcher.beginScoped(
                        supportedScopedModifiers(modifierChain.scoped, bridge).map { modifier ->
                            if (modifier is ScopedVisualModifier.History) {
                                modifier.copy(elementId = historyElementId)
                            } else {
                                modifier
                            }
                        },
                        absX,
                        absY,
                        absolute.width(),
                        absolute.height(),
                        renderSink,
                    )
                    try {
                        // #8 backdrop-filter：平台支持时，在元素自身背景/内容绘制【之前】分派单点 paint modifier。
                        // 它会就地采样元素矩形背后已绘制内容做高斯、画到元素之下（毛玻璃）。
                        // 不是 begin/end 区间，故此处一次性分派即可；矩形取元素绝对包围盒。
                        // backdropBlur=0（默认）或平台不支持时不触碰 backdrop 接缝（零回归）。
                        if (modifierChain.backdropPaint.isNotEmpty() && bridge.supportsBackdropFilter()) {
                            ModifierDispatcher.dispatch(
                                modifierChain.backdropPaint,
                                absX,
                                absY,
                                absolute.width(),
                                absolute.height(),
                                renderSink,
                            )
                        }
                        val renderer = registry.lookup(element)

                        // ScrollableContainer 特化：背景 → 子节点裁剪绘制（按 contentOffset 偏移）→ 滚动条
                        if (element is ScrollableContainer<*>) {
                            // 单节点渲染隔离：滚动容器背景/滚动条绘制抛异常时记 ERROR 中文日志并跳过本节点视觉，
                            // 但仍继续递归其子节点与兄弟节点（不崩整帧）。
                            try {
                                if (renderer is ScrollableContainerRenderer) {
                                    renderer.renderBackground(element, absolute, bridge)
                                } else if (renderer != null) {
                                    renderer.render(element, absolute, bridge, context)
                                }
                            } catch (t: Throwable) {
                                logRenderFailure(element, absolute, t)
                            }
                            val clip = passState.transformedBox(absX, absY, absolute.width(), absolute.height())
                            bridge.pushClip(clip[0], clip[1], clip[2], clip[3])
                            // 基础起点委托 ChildOrigin（单一权威）；渲染端在此之上叠加 sticky-pin / pushClip 逻辑
                            val childOffsetX = ChildOrigin.x(element, absX)
                            val childOffsetY = ChildOrigin.y(element, absY)
                            for (child in childrenSnapshot(element)) {
                                // Phase 3 StickyHeader：sticky 子节点滚出 viewport 顶部时固定在 absY+stickyOffset。
                                var childY = childOffsetY
                                if (child.sticky()) {
                                    val childBox = layout.boxOf(child)
                                    if (childBox != null) {
                                        val pinTop = absY + child.stickyOffset()
                                        val naturalTop = childOffsetY + childBox.y()
                                        if (naturalTop < pinTop) {
                                            // 平移子原点，使 childY + childBox.y() == pinTop。
                                            childY = pinTop - childBox.y()
                                        }
                                    }
                                }
                                renderRecursive(
                                    child, layout, bridge, registry, context, childOffsetX, childY,
                                    overlays, tooltip, passState, nodePaths, depth + 1, slotAncestor || element is Slot,
                                )
                            }
                            bridge.popClip()
                            // 滚动条绘制同样隔离：抛异常记 ERROR 中文日志并跳过，不影响已绘制内容。
                            if (renderer is ScrollableContainerRenderer) {
                                try {
                                    renderer.renderScrollbar(element, absolute, bridge)
                                } catch (t: Throwable) {
                                    logRenderFailure(element, absolute, t)
                                }
                            }
                            return@use
                        }

                        if (renderer != null) {
                            // Slot 子树内的单层渲染器按 aboveItem 语义提升到 OVERLAY 层，盖过物品图标（issue #47）。
                            val layer = resolveLayer(renderer.layer(), element.aboveItem() || slotAncestor)
                            if (layer != null && bridge is LayerBufferingBridge) {
                                bridge.beginLayer(layer)
                            }
                            // 单节点渲染隔离：坏元素抛异常时记 ERROR 中文日志并跳过，继续渲染其兄弟（不崩整帧）。
                            renderNodeIsolated(renderer, element, absolute, bridge, context)
                        }
                        if (element is Dropdown && element.expanded()) {
                            overlays.add(element, absolute, passState.snapshot(), nodePaths?.get(element))
                        }

                        // TabView 特化：压裁剪到 content 区（去掉 header）+ 子节点原点 +HEADER_HEIGHT。
                        // 基础起点委托 ChildOrigin（单一权威）
                        if (element is TabView) {
                            val bodyTop = ChildOrigin.y(element, absY)
                            val bodyH = max(0f, absolute.height() - element.effectiveHeaderHeight())
                            val clip = passState.transformedBox(absX, bodyTop, absolute.width(), bodyH)
                            bridge.pushClip(clip[0], clip[1], clip[2], clip[3])
                            for (child in childrenSnapshot(element)) {
                                renderRecursive(
                                    child, layout, bridge, registry, context, absX, bodyTop,
                                    overlays, tooltip, passState, nodePaths, depth + 1, slotAncestor || element is Slot,
                                )
                            }
                            bridge.popClip()
                            return@use
                        }

                        // 背景粒子（FR-UI-031 layer=back）：元素自身 renderer 已绘制表面/内容，子节点之前叠一层。
                        emitParticleLayers(
                            modifierChain.particleLayers,
                            wantBack = true,
                            element,
                            particleElementId,
                            context,
                            renderSink,
                            absX,
                            absY,
                            absolute,
                        )
                        // overflow=hidden 普通容器：裁剪子树到自身包围盒（裁剪框已在 push 前算好）。
                        val overflowClipped = overflowClip != null
                        if (overflowClipped) {
                            bridge.pushClip(overflowClip!![0], overflowClip[1], overflowClip[2], overflowClip[3])
                        }
                        val sorted = ArrayList(childrenSnapshot(element))
                        sorted.sortBy { it.zIndex() }
                        for (child in sorted) {
                            renderRecursive(
                                child, layout, bridge, registry, context, absX, absY,
                                overlays, tooltip, passState, nodePaths, depth + 1, slotAncestor || element is Slot,
                            )
                        }
                        if (overflowClipped) {
                            bridge.popClip()
                        }
                        // 前景粒子（FR-UI-031 layer=front，默认）：在子节点之后发命令，粒子叠在最上层。
                        emitParticleLayers(
                            modifierChain.particleLayers,
                            wantBack = false,
                            element,
                            particleElementId,
                            context,
                            renderSink,
                            absX,
                            absY,
                            absolute,
                        )
                    } finally {
                        scopedScope.close()
                    }
                } finally {
                    FontAccess.popElement(prevFont)
                }
            }
        }
    }

    private fun runtimeNodePaths(
        root: UIElement<*>,
        bridge: UiRenderBridge,
    ): Map<UIElement<*>, String>? {
        val capture = bridge as? LayerBufferingBridge ?: return null
        return if (capture.capturesRuntimeNodePath()) RuntimeNodePaths.collect(root) else null
    }

    /**
     * 分派元素上的 FR-UI-031 粒子层（ADR-0071）。按 [wantBack] 过滤 layer=back / front，
     * 每个匹配的粒子层发一条延迟绘制命令；ordinal 取其在 particleLayers 中的声明序号（跨帧稳定、隔离多发射器）。
     * 鼠标坐标取自 [ctx]（无鼠标时为 NaN，粒子层内关闭 hover 斥力）。
     */
    @Suppress("LongParameterList")
    private fun emitParticleLayers(
        layers: List<VisualModifier.ParticleLayer>,
        wantBack: Boolean,
        element: UIElement<*>,
        elementId: String,
        ctx: RenderContext,
        sink: RenderSink,
        x: Float,
        y: Float,
        box: Box,
    ) {
        if (layers.isEmpty()) return
        val states = particleStatesOf(element)
        val mouseX = ctx.mouseX()
        val mouseY = ctx.mouseY()
        layers.forEachIndexed { index, layer ->
            val isBack = layer.layer.equals("back", ignoreCase = true)
            if (isBack != wantBack) return@forEachIndexed
            sink.particleLayer(x, y, box.width(), box.height(), elementId, index, layer, states, mouseX, mouseY)
        }
    }

    /** 计算粒子层所需的交互态（驱动 burst trigger 与 hover 判定），与 BoxRenderer.materialStatesOf 同源。 */
    private fun particleStatesOf(element: UIElement<*>): Set<MaterialInteractionState> {
        val states = mutableSetOf<MaterialInteractionState>()
        if (element.hovered()) states += MaterialInteractionState.HOVERED
        if (element.pressed()) states += MaterialInteractionState.PRESSED
        if (element.focused()) states += MaterialInteractionState.FOCUSED
        return states
    }

    /**
     * 取子节点快照，防止渲染遍历期间数据驱动容器 / VirtualList 在其他线程修改 children 导致
     * ConcurrentModificationException。
     */
    // 用 Kotlin 的 toList() 拍只读副本快照（jvm8 兼容，替代 Java 10+ 的 List.copyOf）
    private fun childrenSnapshot(element: UIElement<*>): List<UIElement<*>> = element.children().toList()

    /**
     * 计算全量滚动容器的 contentHeight：取所有子节点 (y + height) 的最大值，
     * 再加上容器自身的 padding.bottom，作为内容总高度。
     *
     * 子节点 box 的 y 由布局引擎给出（相对父原点，已含 padding.top），
     * 故只需在末端补一次 padding.bottom 即可还原“内容真实占用高度”。
     */
    private fun computeContentHeight(sc: ScrollableContainer<*>, layout: LayoutResult): Float {
        var maxBottom = 0f
        for (child in sc.children()) {
            val cb = layout.boxOf(child) ?: continue
            val bottom = cb.y() + cb.height()
            if (bottom > maxBottom) maxBottom = bottom
        }
        return maxBottom + sc.layoutStyle().padding.bottom()
    }

    /**
     * 兑现滚动容器的 scrollIntoView 延迟请求：在直接子节点中按 id 查找目标，
     * 取其相对容器内容原点的局部 box，换算为目标 scroll 并触发。
     *
     * 子节点 box 由布局引擎给出（相对容器原点，已含 padding/当前 contentOffset 偏移），
     * 需要减去当前 contentOffset 还原为“内容坐标系”下的局部位置。
     */
    private fun resolvePendingScrollIntoView(sc: ScrollableContainer<*>, layout: LayoutResult) {
        val targetId = sc.pendingScrollIntoViewId() ?: return
        for (child in sc.children()) {
            if (targetId != child.id()) {
                continue
            }
            val cb = layout.boxOf(child) ?: return
            // 布局给出的 child.x/y 是相对容器原点（未含 scroll 偏移）的局部坐标。
            sc.resolvePendingScrollIntoView(cb.x(), cb.y(), cb.width(), cb.height())
            return
        }
        // 未找到目标子节点（可能是尚未实例化的虚拟项）：清除 pending，避免每帧重试。
        sc.clearPendingScrollIntoView()
    }
}
